import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../api";

const CAREGIVER_TYPES = [
  "Personal Care Service Provider", "Homemaker", "Companion", "Licensed Nursing Assistant",
  "Certified Nursing Assistant", "Home Health Aide", "Registered Nurse", "Licensed Practical Nurse",
  "Transportation Driver",
];
const SHIFT_OPTIONS = ["Morning", "Afternoon", "Evening", "Overnight"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
const inputClass = "w-full border rounded px-2 py-1.5 text-sm";

export default function EmployeeForm() {
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(employeeId);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    salutation: "", first_name: "", middle_name: "", last_name: "", suffix: "", other_names: "", preferred_name: "",
    gender: "", marital_status: "", date_of_birth: "", ssn: "", ssn_last4: "",
    national_id: "", ssnit_number: "", tin_number: "",
    phone: "", home_phone: "", personal_email: "", work_email: "", email: "",
    address: "", city: "", state: "NH", zip: "",
    next_of_kin: "", next_of_kin_relationship: "", next_of_kin_phone: "",
    next_of_kin_alt_phone: "", next_of_kin_email: "", next_of_kin_address: "",
    bank_name: "", bank_account: "", mobile_money: "",
    staff_category: searchParams.get("category") === "field" ? "Field Duty Staff" : "Office Staff",
    department: "", position: "", caregiver_type: "",
    employment_type: "", employment_date: "", employment_status: "Active",
    basic_salary: "", commission_rate: "",
    hourly_pay_rate: "", mileage_rate: "",
    driver_license_number: "", driver_license_state: "", driver_license_expiration: "",
    has_vehicle: false, vehicle_insurance_expiration: "", travel_radius_miles: "",
    live_in_available: false, accepts_emergency_shifts: false,
    max_weekly_hours: "", min_shift_hours: "",
    remarks: "", notes: "",
    branch_office: "", time_tracking_id: "", id_card_issue_date: "", id_card_expiration_date: "",
    username: "", portal_status: "Disabled", mobile_app_enabled: false,
    password_reset_required: true, mfa_enabled: false, preferred_notification: "Email",
    lna_license_number: "", lna_license_expiration: "", lna_license_status: "",
    monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api.employees.get(employeeId).then((e) => {
      setForm((f) => ({
        ...f,
        ...e,
        has_vehicle: !!e.has_vehicle,
        live_in_available: !!e.live_in_available,
        accepts_emergency_shifts: !!e.accepts_emergency_shifts,
        mobile_app_enabled: !!e.mobile_app_enabled,
        password_reset_required: !!e.password_reset_required,
        mfa_enabled: !!e.mfa_enabled,
        ssn: "",
        ...DAYS.reduce((acc, d) => ({ ...acc, [d]: e.availability?.[d] || "" }), {}),
      }));
    }).catch((err) => setError(err.message));
  }, [employeeId, isEdit]);

  const isFieldDuty = form.staff_category === "Field Duty Staff";
  const isTerminated = form.employment_status === "Terminated";

  const toggleShift = (day, shift) => {
    const current = (form[day] || "").split(",").filter(Boolean);
    const next = current.includes(shift) ? current.filter((s) => s !== shift) : [...current, shift];
    setForm({ ...form, [day]: next.join(",") });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      has_vehicle: form.has_vehicle ? 1 : 0,
      live_in_available: form.live_in_available ? 1 : 0,
      accepts_emergency_shifts: form.accepts_emergency_shifts ? 1 : 0,
      mobile_app_enabled: form.mobile_app_enabled ? 1 : 0,
      password_reset_required: form.password_reset_required ? 1 : 0,
      mfa_enabled: form.mfa_enabled ? 1 : 0,
    };
    try {
      if (isEdit) {
        const res = await api.employees.update(employeeId, payload);
        if (res.portal_account_created) {
          if (res.portal_email_sent) {
            alert(`Portal account created and login details were emailed to the employee.`);
          } else {
            alert(`Portal account created.\n\nTemporary password: ${res.portal_temp_password}\n\nThe welcome email did NOT send (${res.portal_email_error || "email not configured"}). Please share this password with the employee directly.`);
          }
        }
        navigate(`/admin/employees/${employeeId}`);
      } else {
        const res = await api.employees.create(payload);
        navigate(`/admin/employees/${res.employee_id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Link to="/admin/employees" className="text-sm hover:underline" style={{ color: "#154E93" }}>← Back to Employees</Link>
      <h1 className="text-2xl font-bold mt-2 mb-6" style={{ color: "#154E93" }}>
        {isEdit ? "Edit Employee" : "Add Employee"}
      </h1>

      {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Staff Category</h2>
          <div className="flex gap-3">
            {["Office Staff", "Field Duty Staff"].map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setForm({ ...form, staff_category: cat })}
                className={`px-4 py-2 text-sm rounded border ${form.staff_category === cat ? "text-white" : "text-gray-600"}`}
                style={form.staff_category === cat ? { backgroundColor: "#154E93", borderColor: "#154E93" } : {}}
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Field Duty Staff unlocks caregiver-specific sections below (transportation, availability) and tabs on their profile (skills, documents/compliance, client assignments).
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="mb-4">
            <h2 className="font-semibold" style={{ color: "#154E93" }}>Identity & Personal Information</h2>
            <p className="text-xs text-gray-500 mt-1">Legal identity, preferred name, and protected HR information.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Salutation">
              <select className={inputClass} value={form.salutation} onChange={(e) => setForm({ ...form, salutation: e.target.value })}>
                <option value="">—</option><option>Mr.</option><option>Ms.</option><option>Mrs.</option><option>Dr.</option>
              </select>
            </Field>
            <Field label="First Name"><input required className={inputClass} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Middle Name"><input className={inputClass} value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} /></Field>
            <Field label="Last Name"><input required className={inputClass} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
            <Field label="Suffix"><input className={inputClass} placeholder="Jr., Sr., III" value={form.suffix} onChange={(e) => setForm({ ...form, suffix: e.target.value })} /></Field>
            <Field label="Preferred Name"><input className={inputClass} value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} /></Field>
            <Field label="Gender">
              <select className={inputClass} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                <option value="">—</option><option>Male</option><option>Female</option><option>Other</option><option>Prefer not to say</option>
              </select>
            </Field>
            <Field label="Marital Status">
              <select className={inputClass} value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })}>
                <option value="">—</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option><option>Separated</option><option>Prefer not to say</option>
              </select>
            </Field>
            <Field label="Date of Birth"><input type="date" className={inputClass} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></Field>
            <Field label={isEdit && form.ssn_last4 ? `SSN (currently XXX-XX-${form.ssn_last4})` : "Social Security Number"}>
              <input className={inputClass} autoComplete="off" placeholder={isEdit ? "Leave blank to keep current SSN" : "000-00-0000"} value={form.ssn} onChange={(e) => setForm({ ...form, ssn: e.target.value })} />
            </Field>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3">SSN is encrypted before storage and is never returned in the regular employee profile response.</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Contact & Address</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Mobile Phone"><input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Home Phone"><input className={inputClass} value={form.home_phone} onChange={(e) => setForm({ ...form, home_phone: e.target.value })} /></Field>
            <Field label="Personal Email"><input type="email" className={inputClass} value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} /></Field>
            <Field label="Work Email"><input type="email" className={inputClass} value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} /></Field>
            <Field label="Street Address" className="md:col-span-2"><input className={inputClass} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="City"><input className={inputClass} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><input className={inputClass} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
            <Field label="ZIP Code"><input className={inputClass} value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></Field>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Emergency Contact</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Full Name"><input className={inputClass} value={form.next_of_kin} onChange={(e) => setForm({ ...form, next_of_kin: e.target.value })} /></Field>
            <Field label="Relationship"><input className={inputClass} value={form.next_of_kin_relationship} onChange={(e) => setForm({ ...form, next_of_kin_relationship: e.target.value })} /></Field>
            <Field label="Primary Phone"><input className={inputClass} value={form.next_of_kin_phone} onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })} /></Field>
            <Field label="Alternate Phone"><input className={inputClass} value={form.next_of_kin_alt_phone} onChange={(e) => setForm({ ...form, next_of_kin_alt_phone: e.target.value })} /></Field>
            <Field label="Email"><input type="email" className={inputClass} value={form.next_of_kin_email} onChange={(e) => setForm({ ...form, next_of_kin_email: e.target.value })} /></Field>
            <Field label="Address" className="md:col-span-3"><input className={inputClass} value={form.next_of_kin_address} onChange={(e) => setForm({ ...form, next_of_kin_address: e.target.value })} /></Field>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Employment</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Department"><input className={inputClass} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
            <Field label="Branch / Office"><input className={inputClass} value={form.branch_office} onChange={(e) => setForm({ ...form, branch_office: e.target.value })} /></Field>
            <Field label="Supervisor / Manager"><input className={inputClass} value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} /></Field>
            <Field label="Time Tracking ID"><input className={inputClass} value={form.time_tracking_id} onChange={(e) => setForm({ ...form, time_tracking_id: e.target.value })} /></Field>
            <Field label="Probation End Date"><input type="date" className={inputClass} value={form.probation_end} onChange={(e) => setForm({ ...form, probation_end: e.target.value })} /></Field>
            <Field label="ID Card Issue Date"><input type="date" className={inputClass} value={form.id_card_issue_date} onChange={(e) => setForm({ ...form, id_card_issue_date: e.target.value })} /></Field>
            <Field label="ID Card Expiration Date"><input type="date" className={inputClass} value={form.id_card_expiration_date} onChange={(e) => setForm({ ...form, id_card_expiration_date: e.target.value })} /></Field>
            <Field label="Position"><input className={inputClass} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></Field>
            {isFieldDuty && (
              <Field label="Caregiver Type">
                <select className={inputClass} value={form.caregiver_type} onChange={(e) => setForm({ ...form, caregiver_type: e.target.value })}>
                  <option value="">—</option>
                  {CAREGIVER_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
            )}
            {isFieldDuty && /Licensed Nursing Assistant|\bLNA\b/i.test(`${form.caregiver_type} ${form.position}`) && (
              <>
                <Field label="NH LNA License Number"><input className={inputClass} value={form.lna_license_number} onChange={(e) => setForm({ ...form, lna_license_number: e.target.value })} /></Field>
                <Field label="LNA License Expiration"><input type="date" className={inputClass} value={form.lna_license_expiration} onChange={(e) => setForm({ ...form, lna_license_expiration: e.target.value })} /></Field>
                <Field label="LNA License Status">
                  <select className={inputClass} value={form.lna_license_status} onChange={(e) => setForm({ ...form, lna_license_status: e.target.value })}>
                    <option value="">—</option><option>Active</option><option>Pending Verification</option><option>Expired</option><option>Suspended</option>
                  </select>
                </Field>
              </>
            )}
            <Field label="Employment Type">
              <select className={inputClass} value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
                <option value="">—</option>
                <option>Full-time</option><option>Part-time</option><option>Per diem</option>
                <option>On-call</option><option>Live-in</option><option>Contract</option><option>Intern</option>
              </select>
            </Field>
            <Field label="Hire Date"><input type="date" className={inputClass} value={form.employment_date} onChange={(e) => setForm({ ...form, employment_date: e.target.value })} /></Field>
            <Field label="Employment Status">
              {isTerminated ? (
                <div className="text-sm text-gray-500 py-1.5">
                  Terminated — use <Link to={`/admin/employees/${employeeId}`} className="underline" style={{ color: "#154E93" }}>Reinstate</Link> on the profile page to change this.
                </div>
              ) : (
                <select className={inputClass} value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })}>
                  <option>Active</option><option>On Leave</option><option>Suspended</option>
                </select>
              )}
            </Field>
            {isFieldDuty ? (
              <>
                <Field label="Hourly Pay Rate"><input type="number" step="0.01" className={inputClass} value={form.hourly_pay_rate} onChange={(e) => setForm({ ...form, hourly_pay_rate: e.target.value })} /></Field>
                <Field label="Mileage Rate"><input type="number" step="0.01" className={inputClass} value={form.mileage_rate} onChange={(e) => setForm({ ...form, mileage_rate: e.target.value })} /></Field>
              </>
            ) : (
              <>
                <Field label="Basic Salary"><input type="number" step="0.01" className={inputClass} value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} /></Field>
                <Field label="Commission Rate"><input type="number" step="0.01" className={inputClass} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} /></Field>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div><h2 className="font-semibold" style={{ color: "#154E93" }}>Employee Portal & Mobile Access</h2><p className="text-xs text-gray-500 mt-1">Foundation for shift acceptance, compliance uploads, training, and employee self-service.</p></div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${form.portal_status === "Active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>{form.portal_status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Username"><input className={inputClass} autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, "") })} /></Field>
            <Field label="Portal Status">
              <select className={inputClass} value={form.portal_status} onChange={(e) => setForm({ ...form, portal_status: e.target.value })}>
                <option>Disabled</option><option>Invited</option><option>Active</option><option>Locked</option>
              </select>
            </Field>
            <Field label="Preferred Notifications">
              <select className={inputClass} value={form.preferred_notification} onChange={(e) => setForm({ ...form, preferred_notification: e.target.value })}>
                <option>Email</option><option>SMS</option><option>Both</option>
              </select>
            </Field>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.mobile_app_enabled} onChange={(e) => setForm({ ...form, mobile_app_enabled: e.target.checked })} /> Mobile app enabled</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.password_reset_required} onChange={(e) => setForm({ ...form, password_reset_required: e.target.checked })} /> Require password reset</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.mfa_enabled} onChange={(e) => setForm({ ...form, mfa_enabled: e.target.checked })} /> MFA enabled</label>
            </div>
          </div>
          {isEdit && <div className="mt-3 text-xs text-gray-500">Last login: {form.last_login ? new Date(form.last_login).toLocaleString() : "Never"}</div>}
        </div>

        {isFieldDuty && (
          <>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Transportation</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
                <Field label="Driver's License #"><input className={inputClass} value={form.driver_license_number} onChange={(e) => setForm({ ...form, driver_license_number: e.target.value })} /></Field>
                <Field label="License State"><input className={inputClass} value={form.driver_license_state} onChange={(e) => setForm({ ...form, driver_license_state: e.target.value })} /></Field>
                <Field label="License Expiration"><input type="date" className={inputClass} value={form.driver_license_expiration} onChange={(e) => setForm({ ...form, driver_license_expiration: e.target.value })} /></Field>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_vehicle} onChange={(e) => setForm({ ...form, has_vehicle: e.target.checked })} />
                  Has own vehicle
                </label>
                <Field label="Vehicle Insurance Expiration"><input type="date" className={inputClass} value={form.vehicle_insurance_expiration} onChange={(e) => setForm({ ...form, vehicle_insurance_expiration: e.target.value })} /></Field>
                <Field label="Travel Radius (miles)"><input type="number" className={inputClass} value={form.travel_radius_miles} onChange={(e) => setForm({ ...form, travel_radius_miles: e.target.value })} /></Field>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Availability</h2>
              <div className="space-y-2 mb-4">
                {DAYS.map((day) => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-24 text-sm capitalize">{day}</span>
                    <div className="flex gap-2 flex-wrap">
                      {SHIFT_OPTIONS.map((shift) => {
                        const active = (form[day] || "").split(",").includes(shift);
                        return (
                          <button
                            type="button"
                            key={shift}
                            onClick={() => toggleShift(day, shift)}
                            className={`px-2.5 py-1 text-xs rounded border ${active ? "text-white" : "text-gray-600"}`}
                            style={active ? { backgroundColor: "#154E93", borderColor: "#154E93" } : {}}
                          >
                            {shift}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Max Weekly Hours"><input type="number" className={inputClass} value={form.max_weekly_hours} onChange={(e) => setForm({ ...form, max_weekly_hours: e.target.value })} /></Field>
                <Field label="Min Shift Length (hrs)"><input type="number" className={inputClass} value={form.min_shift_hours} onChange={(e) => setForm({ ...form, min_shift_hours: e.target.value })} /></Field>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input type="checkbox" checked={form.live_in_available} onChange={(e) => setForm({ ...form, live_in_available: e.target.checked })} />
                  Available for live-in
                </label>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input type="checkbox" checked={form.accepts_emergency_shifts} onChange={(e) => setForm({ ...form, accepts_emergency_shifts: e.target.checked })} />
                  Accepts emergency shifts
                </label>
              </div>
            </div>
          </>
        )}

        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Banking</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Bank Name"><input className={inputClass} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Field>
            <Field label="Bank Account"><input className={inputClass} value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></Field>
            <Field label="Mobile Money"><input className={inputClass} value={form.mobile_money} onChange={(e) => setForm({ ...form, mobile_money: e.target.value })} /></Field>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="font-semibold mb-3" style={{ color: "#154E93" }}>Notes</h2>
          <textarea rows={3} className={inputClass} value={isFieldDuty ? form.notes : form.remarks} onChange={(e) => setForm({ ...form, [isFieldDuty ? "notes" : "remarks"]: e.target.value })} />
        </div>

        <div className="flex justify-end gap-2">
          <Link to="/admin/employees" className="px-4 py-2 text-sm rounded border">Cancel</Link>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded text-white" style={{ backgroundColor: "#154E93" }}>
            {saving ? "Saving…" : "Save Employee"}
          </button>
        </div>
      </form>
    </div>
  );
}
